import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { EventConfig, Participant, PurchaseAction, Scene } from "@/lib/types/config";
import type { ActionStates } from "@/lib/types/api";
import { resolveAssetUrl } from "@/components/ui";
import { luminance, readableTextOn } from "@/lib/utils/color";

export type BoothScreen =
  | "welcome" | "mode" | "participants" | "vibes" | "capture"
  | "processing" | "result" | "completion";

/** Which product the guest is buying into on this run through the booth. */
export type CaptureMode = "ai_photo_booth" | "snapshot";

interface BoothState {
  config: EventConfig;
  slug: string;
  screen: BoothScreen;
  mode: CaptureMode;
  participant: Participant | null;
  scene: Scene | null;
  capture: string | null;
  filterSlug: string;
  guestFields: Record<string, string>;
  generationId: number | null;
  snapshotId: number | null;
  shareToken: string | null;
  resultUrl: string | null;
  /** Price and entitlement per action for the photo on screen. */
  actions: ActionStates | null;
}

interface BoothContextValue extends BoothState {
  go: (screen: BoothScreen) => void;
  goBack: () => void;
  registerBackHandler: (handler: (() => boolean) | null) => () => void;
  selectParticipant: (participant: Participant | null) => void;
  selectScene: (scene: Scene) => void;
  setCapture: (dataUrl: string | null) => void;
  setMode: (mode: CaptureMode) => void;
  setFilter: (slug: string) => void;
  setGuestField: (key: string, value: string) => void;
  startGeneration: (payload: { generationId: number; shareToken: string }) => void;
  /** Capture finished — show the photo and its priced actions. */
  showResult: (payload: {
    generationId?: number; snapshotId?: number; shareToken: string;
    url: string; actions: ActionStates | null;
  }) => void;
  /** Payment cleared (or the action was free) — unlock it and go back. */
  grantAction: (action: PurchaseAction) => void;
  /** Replaces the entitlement map once the result screen has fetched it. */
  setActions: (actions: ActionStates) => void;
  restart: () => void;
  /** The next screen after welcome, honouring mode choice and skip_participants. */
  firstStep: BoothScreen;
  /** Where to go once a capture mode is settled. */
  stepAfterMode: BoothScreen;
  availableModes: CaptureMode[];
  /** Blueprint-driven booth vocabulary. */
  say: (key: string, fallback: string) => string;
  copy: (key: string, fallback: string) => string;
  playCue: (key: "welcome" | "participant_selection" | "processing" | "result" | "completion") => void;
}

const BoothContext = createContext<BoothContextValue | null>(null);

export function BoothProvider({
  config, slug, children,
}: { config: EventConfig; slug: string; children: ReactNode }) {
  // The blueprint has the final say on whether this format has sides to pick.
  const participantSelection =
    config.experience?.participant_selection ??
    !(config.workflow?.skip_participants || config.participants.length === 0);

  const availableModes = useMemo<CaptureMode[]>(() => {
    const blueprintModes = config.experience?.capture_modes ?? [];
    const eventModes = config.event.service_types ?? [];
    const supportsAi = blueprintModes.length > 0
      ? blueprintModes.includes("ai_photo_booth") && eventModes.includes("ai_photo_booth")
      : eventModes.includes("ai_photo_booth");
    const supportsSnapshot = blueprintModes.length > 0
      ? blueprintModes.includes("snapshot") && eventModes.includes("snapshot")
      : eventModes.includes("snapshot");

    const result: CaptureMode[] = [];
    if (supportsAi) result.push("ai_photo_booth");
    if (supportsSnapshot && config.snapshot?.enabled) result.push("snapshot");

    // If snapshot is enabled and the booth has no other selectable product,
    // allow snapshot-only mode rather than defaulting to AI.
    if (result.length === 0) {
      return config.snapshot?.enabled ? ["snapshot"] : ["ai_photo_booth"];
    }

    return result;
  }, [config]);

  const stepAfterMode: BoothScreen = participantSelection ? "participants" : "vibes";

  // Only stop to ask which product they want when there's genuinely a choice.
  const firstStep: BoothScreen = availableModes.length > 1 ? "mode" : stepAfterMode;

  const [state, setState] = useState<BoothState>({
    config, slug, screen: "welcome",
    mode: availableModes[0] ?? "ai_photo_booth",
    participant: null, scene: null, capture: null,
    filterSlug: config.snapshot?.filters?.[0]?.slug ?? "none",
    guestFields: {},
    generationId: null, snapshotId: null,
    shareToken: null, resultUrl: null,
    actions: null,
  });
  const screenHistory = useRef<BoothScreen[]>([]);
  const lastScreen = useRef<BoothScreen>("welcome");
  const navigatingBack = useRef(false);
  const backHandler = useRef<(() => boolean) | null>(null);

  useEffect(() => {
    if (state.screen === lastScreen.current) return;
    if (navigatingBack.current) navigatingBack.current = false;
    else if (screenHistory.current.at(-1) !== lastScreen.current) screenHistory.current.push(lastScreen.current);
    lastScreen.current = state.screen;
  }, [state.screen]);

  // Config can change under a running booth when an operator republishes; the
  // guest mid-flow shouldn't be interrupted, but the next one gets the update.
  useEffect(() => {
    setState((current) => ({ ...current, config }));
  }, [config]);

  const audio = useAudioCues(config);

  const go = useCallback((screen: BoothScreen) => {
    setState((current) => {
      if (current.screen === screen) return current;
      navigatingBack.current = false;
      return { ...current, screen };
    });
  }, []);

  const registerBackHandler = useCallback((handler: (() => boolean) | null) => {
    backHandler.current = handler;
    return () => {
      if (backHandler.current === handler) backHandler.current = null;
    };
  }, []);

  const goBack = useCallback(() => {
    if (backHandler.current?.()) return;
    let previous = screenHistory.current.pop();
    while (previous === lastScreen.current) previous = screenHistory.current.pop();
    if (!previous) return;
    navigatingBack.current = true;
    setState((current) => {
      if (current.screen === previous) {
        navigatingBack.current = false;
        return current;
      }
      return { ...current, screen: previous };
    });
  }, []);

  const selectParticipant = useCallback((participant: Participant | null) => {
    // The flow is event-configured. Participant-only booths (such as Kids
    // Dream Jobs) can go straight to capture without a vibe selection.
    const skipVibes = config.workflow?.skip_vibes === true;
    setState((current) => ({
      ...current,
      participant,
      scene: skipVibes ? current.scene ?? config.scenes[0] ?? null : current.scene,
      // Snapshot's session designer owns its own layout/filter step. Keep it on
      // the snapshot branch even when an AI-oriented blueprint skips vibes.
      screen: current.mode === "snapshot" ? "vibes" : skipVibes ? "capture" : "vibes",
    }));
  }, [config.scenes, config.workflow?.skip_vibes]);

  const selectScene = useCallback((scene: Scene) => {
    setState((current) => ({ ...current, scene, screen: "capture" }));
  }, []);

  const setCapture = useCallback((dataUrl: string | null) => {
    setState((current) => ({ ...current, capture: dataUrl }));
  }, []);

  const setMode = useCallback((mode: CaptureMode) => {
    setState((current) => ({ ...current, mode }));
  }, []);

  const setFilter = useCallback((filterSlug: string) => {
    setState((current) => ({ ...current, filterSlug }));
  }, []);

  const setGuestField = useCallback((key: string, value: string) => {
    setState((current) => ({ ...current, guestFields: { ...current.guestFields, [key]: value } }));
  }, []);

  const showResult = useCallback((payload: {
    generationId?: number; snapshotId?: number; shareToken: string;
    url: string;
    /** Null means "not known yet" — the result screen fetches it. It must
        never be treated as "everything is free". */
    actions: ActionStates | null;
  }) => {
    setState((current) => ({
      ...current,
      generationId: payload.generationId ?? current.generationId,
      snapshotId: payload.snapshotId ?? current.snapshotId,
      shareToken: payload.shareToken,
      resultUrl: payload.url,
      actions: payload.actions,
      screen: "result",
    }));
  }, []);

  /** Payment cleared. Unlock just that action — buying a print is not buying
      a download — and return to the photo. */
  const grantAction = useCallback((action: PurchaseAction) => {
    setState((current) => ({
      ...current,
      actions: current.actions
        ? { ...current.actions, [action]: { ...current.actions[action], granted: true } }
        : current.actions,
      screen: "result",
    }));
  }, []);

  const setActions = useCallback((actions: ActionStates) => {
    setState((current) => ({ ...current, actions }));
  }, []);

  /** Hands off to the processing screen, which owns the polling. */
  const startGeneration = useCallback((payload: { generationId: number; shareToken: string }) => {
    setState((current) => ({
      ...current,
      generationId: payload.generationId,
      shareToken: payload.shareToken,
      resultUrl: null,
      screen: "processing",
    }));
  }, []);

  const restart = useCallback(() => {
    screenHistory.current = [];
    lastScreen.current = "welcome";
    navigatingBack.current = false;
    backHandler.current = null;
    setState((current) => ({
      ...current,
      screen: "welcome", participant: null, scene: null,
      capture: null, guestFields: {},
      generationId: null, snapshotId: null,
      shareToken: null, resultUrl: null,
      actions: null,
    }));
  }, []);

  // Booth vocabulary. Everything guest-facing reads through this so a football
  // booth and a wedding booth can run identical components.
  const say = useCallback((key: string, fallback: string) => {
    const value = config.experience?.vocabulary?.[key];
    return typeof value === "string" && value.trim() ? value : fallback;
  }, [config.experience?.vocabulary]);

  const copy = useCallback((key: string, fallback: string) => {
    const record = (config.assets?.copy ?? {}) as Record<string, string | undefined>;
    return record[key] || fallback;
  }, [config.assets?.copy]);

  const value = useMemo<BoothContextValue>(() => ({
    ...state,
    go, goBack, registerBackHandler, selectParticipant, selectScene, setCapture, setMode, setFilter, setGuestField,
    startGeneration, showResult, grantAction, setActions, restart,
    firstStep, stepAfterMode, availableModes, say, copy, playCue: audio.play,
  }), [
    state, go, goBack, registerBackHandler, selectParticipant, selectScene, setCapture, setMode, setFilter, setGuestField,
    startGeneration, showResult, grantAction, setActions, restart,
    firstStep, stepAfterMode, availableModes, say, copy, audio.play,
  ]);

  return <BoothContext.Provider value={value}>{children}</BoothContext.Provider>;
}

export function useBooth(): BoothContextValue {
  const context = useContext(BoothContext);
  if (!context) throw new Error("useBooth must be used inside a BoothProvider");
  return context;
}

/**
 * Booth audio cues.
 *
 * Browsers block autoplay until the page has been interacted with, so nothing
 * plays before the guest's first tap. Everything here is best-effort: a missing
 * or blocked sound must never interrupt the flow.
 */
function useAudioCues(config: EventConfig) {
  const unlocked = useRef(false);
  const current = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const unlock = () => { unlocked.current = true; };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const play = useCallback((key: "welcome" | "participant_selection" | "processing" | "result" | "completion") => {
    if (!unlocked.current) return;

    const audio = config.assets?.audio ?? {};
    // Older configs use different names for two of these slots.
    const source =
      key === "participant_selection" ? audio.participant_selection ?? audio.team_selection
      : key === "processing" ? audio.processing ?? audio.ai_processing
      : audio[key];

    const url = resolveAssetUrl(source);
    if (!url) return;

    current.current?.pause();
    const element = new Audio(url);
    element.volume = 0.6;
    // Long cues loop; stings play once.
    element.loop = key === "welcome" || key === "processing";
    void element.play().catch(() => undefined);
    current.current = element;
  }, [config.assets?.audio]);

  useEffect(() => () => { current.current?.pause(); }, []);

  return { play };
}

/**
 * Maps the event's branding onto the CSS variables the booth screens read.
 *
 * ## Surfaces are derived, never assumed
 *
 * The booth paints the whole page in the operator's brand colour. Screens then
 * put cards and labels on top — and they used to do that with hardcoded
 * `bg-black/20` and `text-white`, which quietly assumed the operator had chosen
 * a *dark* brand colour.
 *
 * Pick lime, and the vibe cards became lime-on-lime with white text: a screen
 * that looked empty. Nothing errored, nothing logged, and the operator's only
 * clue was that their booth looked broken.
 *
 * So every surface and text colour is now computed from the brand colour's
 * luminance. On a light brand the scrims go dark and the text goes near-black;
 * on a dark brand they invert. Screens read `--booth-surface` and `--booth-ink`
 * and stop guessing.
 */
export function brandingStyle(config: EventConfig): CSSProperties {
  const branding = config.event.branding ?? {};
  const primary = branding.primary_color ?? "#d4f14f";
  const secondary = branding.secondary_color ?? "#0b0d10";
  const sizes = branding.text_sizes ?? {};
  const colors = branding.text_colors ?? {};
  const background = resolveAssetUrl(branding.background_url);

  // Is the page light enough that dark ink and dark scrims are the right call?
  const onLight = luminance(primary) > 0.45;
  const ink = onLight ? "#12100f" : "#ffffff";
  const scrim = onLight ? "0, 0, 0" : "255, 255, 255";

  return {
    "--booth-primary": primary,
    "--booth-secondary": secondary,
    "--booth-headline": colors.headline ?? readableTextOn(primary),
    "--booth-subtitle": colors.subtitle ?? readableTextOn(primary),
    // Was hardcoded #ffffff — invisible on any light brand colour, and the
    // tagline is the line that tells a passer-by what they get.
    "--booth-tagline": colors.tagline ?? readableTextOn(primary),
    "--booth-cta-text": colors.cta ?? readableTextOn(secondary),

    // A card must separate from the page whichever way round the page is.
    "--booth-surface": `rgba(${scrim}, ${onLight ? 0.1 : 0.14})`,
    "--booth-surface-strong": `rgba(${scrim}, ${onLight ? 0.2 : 0.28})`,
    "--booth-line": `rgba(${scrim}, ${onLight ? 0.18 : 0.22})`,
    "--booth-ink": ink,
    "--booth-ink-muted": onLight ? "rgba(18,16,15,0.66)" : "rgba(255,255,255,0.72)",
    // Chips sitting on a CTA-coloured card. Keyed off the secondary colour,
    // because that card is painted with it and may be light or dark.
    "--booth-cta-scrim": luminance(secondary) > 0.45 ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.16)",
    "--booth-headline-size": `${sizes.headline ?? 3}rem`,
    "--booth-subtitle-size": `${sizes.subtitle ?? 1}rem`,
    "--booth-tagline-size": `${sizes.tagline ?? 1.25}rem`,
    "--booth-cta-size": `${sizes.cta ?? 1.25}rem`,
    "--booth-text-gap": `${branding.text_gap ?? 0.5}rem`,
    "--booth-bg-opacity": String(branding.background_opacity ?? 0.12),
    "--booth-bg-image": background ? `url(${background})` : "none",
    "--booth-bg-media": branding.background_media_type ?? "image",
  } as CSSProperties;
}
